import { FC, useState } from 'react'
import { usePois } from '../../hooks/api/usePois.ts'
import { Save, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Poi } from '@/js/types'
import { useAtomValue, useSetAtom } from 'jotai'
import {
    clearPoiEditorAtom,
    effectiveActivePoiAtom,
    markPoiSavedAtom,
    poiDirtyAtom,
    poiSavingAtom,
    setPoiSaveErrorAtom,
    setPoiSavingAtom,
} from '@/js/editor/state/poiAtoms.ts'

interface PoiActionsProps {
    poiToSave: () => Poi
}

export const PoiActions: FC<PoiActionsProps> = ({ poiToSave }) => {
    const activePoi = useAtomValue(effectiveActivePoiAtom)
    const isDraftDirty = useAtomValue(poiDirtyAtom)
    const isSaving = useAtomValue(poiSavingAtom)

    const markPoiSaved = useSetAtom(markPoiSavedAtom)
    const clearPoiEditor = useSetAtom(clearPoiEditorAtom)
    const setPoiSaving = useSetAtom(setPoiSavingAtom)
    const setPoiSaveError = useSetAtom(setPoiSaveErrorAtom)

    const { updatePoi, deletePoi } = usePois()

    const [trashColor, setTrashColor] = useState('var(--color-mm-gray2)')
    const [saveColor, setSaveColor] = useState('var(--color-mm-blue)')

    if (!activePoi) {
        return null
    }

    const handleSave = async () => {
        if (!isDraftDirty || isSaving) {
            return
        }

        setPoiSaving(true)
        setPoiSaveError(null)

        try {
            const savedPoi = await updatePoi(poiToSave())

            markPoiSaved(savedPoi)
            toast.success(`${savedPoi.title} wurde gespeichert.`)
        } catch (error) {
            const saveError = error instanceof Error ? error : new Error('Der POI konnte nicht gespeichert werden.')

            setPoiSaving(false)
            setPoiSaveError(saveError)
            toast.error('Speichern fehlgeschlagen', {
                description: saveError.message,
            })
        }
    }

    const handleDelete = () => {
        deletePoi(activePoi)
            .then(() => {
                toast.warning(`${activePoi.title} wurde gelöscht!`)
                clearPoiEditor()
            })
            .catch((error) => {
                toast.error('Löschen fehlgeschlagen', {
                    description: error instanceof Error ? error.message : 'Der POI konnte nicht gelöscht werden.',
                })
            })
    }

    return (
        <div className={'flex flex-row gap-3'}>
            <Dialog>
                <DialogTrigger>
                    <Trash2
                        color={trashColor}
                        className={'cursor-pointer drop-shadow-xs hover:drop-shadow-md transition-all'}
                        onMouseEnter={() => setTrashColor('var(--color-red-400)')}
                        onMouseLeave={() => setTrashColor('var(--color-mm-gray2)')}
                    />
                </DialogTrigger>
                <DialogContent className={'z-2005 max-w-75'}>
                    <DialogTitle>Eintrag löschen?</DialogTitle>
                    <DialogDescription>
                        Gelöschte Karteneinträge können nicht wiederhergestellt werden.
                    </DialogDescription>
                    <DialogFooter>
                        <Button variant={'destructive'} onClick={handleDelete}>
                            Löschen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Save
                aria-disabled={!isDraftDirty}
                onClick={isDraftDirty ? handleSave : undefined}
                color={isDraftDirty ? 'white' : saveColor}
                className={`cursor-not-allowed drop-shadow-xs hover:drop-shadow-md transition-all rounded ${
                    isDraftDirty ? 'bg-red-300 hover:bg-red-400 animate-[bounce_1s_ease-in-out_2.5] cursor-pointer' : ''
                }`}
                onMouseEnter={() => setSaveColor('var(--color-mm-gray2)')}
                onMouseLeave={() => setSaveColor('var(--color-mm-blue)')}
            />
        </div>
    )
}
